/**
 * Sentry Browser SDK initialization (shared across all verticals)
 *
 * Initializes Sentry as early as possible in the application lifecycle.
 * DSN and environment are read from data attributes on the <html> tag.
 *
 * Usage: Import this file as the first line in each vertical's app.js:
 *   import '../../../common-assets/js/components/sentry_init';
 */

import * as Sentry from '@sentry/browser';

(function initSentry() {
    const htmlElement = document.documentElement;
    const { sentryDsn: dsn, environment, siteCode } = htmlElement.dataset;

    // Skip initialization if DSN is not provided (dev/test environments)
    if (!dsn || dsn === '') {
        console.info('[Sentry] No DSN provided; skipping initialization.');
        return;
    }

    try {
        const isTest = environment === 'test';

        Sentry.init({
            dsn,
            environment: environment || 'production',

            // 100% in test/staging, 10% in production
            sampleRate: isTest ? 1 : 0.1,

            // Tracing: 100% in test/staging, 10% in production
            tracesSampleRate: isTest ? 1 : 0.1,

            // Add site code as tag for filtering in Sentry UI
            initialScope: {
                tags: {
                    site_code: siteCode || 'unknown',
                },
            },

            // Automatically capture unhandled promise rejections
            integrations: [
                Sentry.browserTracingIntegration({ enabled: false }),
                Sentry.replayIntegration({ enabled: false }),
            ],

            // Filter out noise (third-party scripts, browser extensions)
            ignoreErrors: [
                // Browser extension errors
                'top.GLOBALS',
                'chrome-extension://',
                'moz-extension://',
                // Network errors (handled by backend)
                'Network request failed',
                'NetworkError',
                // Random plugins/extensions
                'ResizeObserver loop limit exceeded',
            ],

            // Only capture errors from our own domains (production + staging + branch)
            // Matches any subdomain: www.nettiauto.com, assets.nettiauto.com, etc.
            allowUrls: [
                /https?:\/\/[^/]*nettiauto\.com/,
                /https?:\/\/[^/]*nettimoto\.com/,
                /https?:\/\/[^/]*nettivene\.com/,
                /https?:\/\/[^/]*nettikone\.com/,
                /https?:\/\/[^/]*nettikaravaani\.com/,
                /https?:\/\/[^.]+\.ajomark\.dev/,
                /https?:\/\/[^.]+\.nettix-aws\.com/,
            ],

            // Attach user context if Alma Tunnus user is logged in
            beforeSend(event) {
                // Check if user info is available in global scope (set by alma_tunnus.js)
                if (globalThis.almaTunnus !== undefined && globalThis.almaTunnus.user) {
                    const { id, username } = globalThis.almaTunnus.user;
                    // Do NOT send email, phone, or other PII
                    return { ...event, user: { id: id || undefined, username: username || undefined } };
                }

                return event;
            },
        });

        console.info('[Sentry] Initialized successfully', { environment, siteCode });
    } catch (error) {
        // Log init failure but don't block app
        console.error('[Sentry] Failed to initialize:', error);
    }
})();
